import type { ComponentPropsWithoutRef } from 'react';
import { useMemo } from 'react';

import {
  useCreateLocationMutation,
  useGetAllLocations,
} from '~entities/locations';
import { type LocationDetails } from '~entities/locations/index';

import Creatable from '~shared/components/select/creatable';

export interface LocationOption {
  label: string | null;
  value: string | null;
}

export interface LocationSelectorProps
  extends Omit<
    ComponentPropsWithoutRef<typeof Creatable>,
    'value' | 'onChange' | 'options' | 'onCreateOption' | 'isLoading'
  > {
  value: LocationOption | null;
  onChange: (value: LocationOption | null) => void;
}

export const LocationSelector = ({
  value,
  onChange,
  ...creatableProps
}: LocationSelectorProps) => {
  const mutation = useCreateLocationMutation();
  const { data, isFetching } = useGetAllLocations();
  
  // Filter out duplicates by fieldName and remove any null/undefined entries
  const options = useMemo(() => {
    if (!data) return [];
    
    return Array.from(
      new Map(
        data
          .filter((location) => location.fieldName && location._id)
          .map((location) => [
            location.fieldName,
            {
              label: location.fieldName,
              value: location._id,
            },
          ]),
      ).values(),
    );
  }, [data]);

  return (
    <Creatable
      {...creatableProps}
      value={value}
      onChange={(option) => {
        const opt = option as LocationOption | null;
        onChange(opt);
      }}
      onCreateOption={async (inputValue: string) => {
        const location = await mutation.mutateAsync({ fieldName: inputValue });
        onChange({ label: location.fieldName, value: location._id });
      }}
      className='min-w-44'
      placeholder='Select Location'
      options={options}
      isLoading={isFetching}
    />
  );
};
